/**
 * Préférences de notification par catégorie (migration 0284, lot E).
 *
 * Ligne absente = push ET email activés (opt-out). MIROIR des catégories :
 * CHECK SQL de `notification_preferences` et `_shared/notification-catalog.ts`
 * (parité testée). Le Feed n'est jamais coupé ; les messages de service
 * (paiement, compte, annulation d'un cours réservé) partent toujours.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { SupabaseClient } from '@supabase/supabase-js';
import { supabase } from '@/lib/supabase';

const db = supabase as unknown as SupabaseClient;

export const NOTIFICATION_CATEGORIES = [
  'devoirs',
  'skillcards',
  'messages',
  'rendez_vous',
  'liens',
  'live',
  'contenus',
  'bilan_parent',
] as const;
export type NotificationCategory = (typeof NOTIFICATION_CATEGORIES)[number];

type Role = 'student' | 'tutor' | 'parent' | 'admin' | string;

interface CategoryCopy {
  label: string;
  /** Description selon le rôle (absente = catégorie masquée pour ce rôle). */
  student?: string;
  tutor?: string;
  parent?: string;
}

export const NOTIFICATION_CATEGORY_COPY: Record<NotificationCategory, CategoryCopy> = {
  devoirs: {
    label: 'Devoirs, exercices et évals',
    student: 'Un prof t’assigne un exercice ; rappel de révision 3 jours et la veille d’une éval.',
    tutor: 'Un élève termine un exercice, lance un défi ou t’envoie un exercice en photo.',
  },
  skillcards: {
    label: 'SKILLCARDS',
    student: 'Cartes envoyées par ton prof, créature évoluée, défi relevé.',
    tutor: 'Nouveaux avis sur tes cartes.',
  },
  messages: {
    label: 'Messages',
    student: 'Nouveau message dans une discussion.',
    tutor: 'Nouveau message dans une discussion.',
    parent: 'Nouveau message dans une discussion.',
  },
  rendez_vous: {
    label: 'Rendez-vous',
    student: 'Rendez-vous programmé, « c’est l’heure ».',
    tutor: 'Début de tes rendez-vous.',
  },
  liens: {
    label: 'Profs et classes',
    student: 'Invitation d’un prof.',
    tutor: 'Invitation acceptée, nouvel élève dans une classe.',
  },
  live: {
    label: 'Live School',
    student: 'Rappels avant une session Live.',
  },
  bilan_parent: {
    label: 'Bilan hebdo de mes enfants',
    parent: 'Chaque dimanche : exercices, HORA et SKILLCARDS de la semaine.',
  },
  contenus: {
    label: 'Nouveaux contenus',
    student: 'Nouvel article publié.',
    tutor: 'Nouvel article publié.',
    parent: 'Nouvel article publié.',
  },
};

export interface NotificationCategoryRow {
  category: NotificationCategory;
  label: string;
  description: string;
}

/** Catégories affichées pour un rôle (admin = vue prof). */
export function categoriesForRole(role: Role | null | undefined): NotificationCategoryRow[] {
  const key = role === 'admin' ? 'tutor' : role === 'tutor' || role === 'parent' ? role : 'student';
  return NOTIFICATION_CATEGORIES.flatMap((category) => {
    const copy = NOTIFICATION_CATEGORY_COPY[category];
    const description = copy[key as 'student' | 'tutor' | 'parent'];
    return description ? [{ category, label: copy.label, description }] : [];
  });
}

export interface NotificationPreference {
  category: NotificationCategory;
  push: boolean;
  email: boolean;
}

/** Valeur effective (ligne absente = tout activé). */
export function preferenceFor(
  rows: readonly NotificationPreference[] | undefined,
  category: NotificationCategory,
): NotificationPreference {
  return rows?.find((r) => r.category === category) ?? { category, push: true, email: true };
}

export const notificationPreferencesQueryKey = (userId: string) =>
  ['notification-preferences', userId] as const;

export function useNotificationPreferences(userId: string | undefined) {
  return useQuery({
    queryKey: notificationPreferencesQueryKey(userId ?? ''),
    enabled: !!userId,
    queryFn: async (): Promise<NotificationPreference[]> => {
      const { data, error } = await db
        .from('notification_preferences')
        .select('category, push, email')
        .eq('user_id', userId!);
      if (error) throw new Error(error.message);
      return (data ?? []) as NotificationPreference[];
    },
  });
}

export function useSetNotificationPreference(userId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (pref: NotificationPreference) => {
      const { error } = await db
        .from('notification_preferences')
        .upsert(
          { user_id: userId, ...pref, updated_at: new Date().toISOString() },
          { onConflict: 'user_id,category' },
        );
      if (error) throw new Error(error.message);
    },
    onMutate: async (pref) => {
      const key = notificationPreferencesQueryKey(userId ?? '');
      qc.setQueryData<NotificationPreference[]>(key, (prev = []) => [
        ...prev.filter((p) => p.category !== pref.category),
        pref,
      ]);
    },
    onSettled: () =>
      void qc.invalidateQueries({ queryKey: notificationPreferencesQueryKey(userId ?? '') }),
  });
}

// ── Visibilité de ma progression SKILLCARDS pour mes camarades (0285) ──────

/** Défaut serveur : oui à partir de 15 ans, non avant ou sans date de naissance. */
export function defaultPeerVisibility(birthdate: string | null | undefined, today = new Date()) {
  if (!birthdate) return false;
  const b = new Date(`${birthdate}T00:00:00`);
  if (Number.isNaN(b.getTime())) return false;
  const limit = new Date(today.getFullYear() - 15, today.getMonth(), today.getDate());
  return b <= limit;
}

export function usePeerVisibility(userId: string | undefined) {
  return useQuery({
    queryKey: ['peer-visibility', userId ?? ''],
    enabled: !!userId,
    queryFn: async (): Promise<boolean> => {
      const { data, error } = await db
        .from('student_profiles')
        .select('birthdate, share_progress_with_peers')
        .eq('id', userId!)
        .maybeSingle();
      if (error) throw new Error(error.message);
      const row = data as {
        birthdate: string | null;
        share_progress_with_peers: boolean | null;
      } | null;
      return row?.share_progress_with_peers ?? defaultPeerVisibility(row?.birthdate);
    },
  });
}

export function useSetPeerVisibility(userId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (visible: boolean) => {
      const { error } = await db
        .from('student_profiles')
        .update({ share_progress_with_peers: visible })
        .eq('id', userId!);
      if (error) throw new Error(error.message);
    },
    onSuccess: (_d, visible) => {
      qc.setQueryData(['peer-visibility', userId ?? ''], visible);
      void qc.invalidateQueries({ queryKey: ['card-circle'] });
    },
  });
}
